/*!
 * BR Size Selector v2.5 — Collection-only, synced with Bloomreach filters
 * - Builds chips from the live "Size" facet (no hardcoded list)
 * - Chip click toggles the real Size checkboxes (fires 'change')
 * - URL kept in sync via ?size=VALUE (VALUE = checkbox value)
 * - Top "Clear" now behaves like full site "Clear all" (all facets)
 * - Also resets chips when left "Clear all" is used
 */

(function () {
  'use strict';

  // Only on collection pages
  if (!/\/collections(\/|$)/.test(location.pathname)) return;
  if (window.__BR_SIZE_SELECTOR_INITIALIZED__) return;
  window.__BR_SIZE_SELECTOR_INITIALIZED__ = true;

  const URL_KEY_SIZE = 'size';
  const STORAGE_KEY = 'br_selected_sizes';

  // Keys your page uses in the URL — we wipe these on full clear
  const URL_FILTER_KEYS = ['size','brand','category','interest','color','gender','team','deal','price','min_price','max_price'];

  // ---------- helpers ----------
  function getSizeFacet() {
    const groups = document.querySelectorAll('.filters-content__group');
    for (const g of groups) {
      const t = g.querySelector('.filters-content__title');
      if (t && /^\s*size\s*$/i.test(t.textContent.trim())) return g;
    }
    return null;
  }

  function readAvailableSizes() {
    const facet = getSizeFacet();
    if (!facet) return [];
    const seen = new Set();
    const out = [];
    facet.querySelectorAll('.filters-content__values label').forEach(lab => {
      const cb = lab.querySelector('input[type="checkbox"]');
      if (!cb) return;
      const value = (cb.value || '').trim();               // canonical value for API & URL
      if (!value || seen.has(value)) return;
      seen.add(value);
      const raw = (lab.querySelector('span')?.textContent || value).trim();
      const label = value === 'ONE SIZE' ? 'One Size' : raw; // nicer label for ONE SIZE
      out.push({ label, value });
    });
    return out;
  }

  function getUrlSelectedSizes() {
    const p = new URLSearchParams(location.search);
    return p.getAll(URL_KEY_SIZE).map(s => s.trim()).filter(Boolean);
  }
  function setUrlSelectedSizes(values) {
    const u = new URL(location.href);
    u.searchParams.delete(URL_KEY_SIZE);
    values.forEach(v => u.searchParams.append(URL_KEY_SIZE, v));
    history.replaceState({}, '', u.toString());
  }
  function clearAllFilterParamsInUrl() {
    const u = new URL(location.href);
    URL_FILTER_KEYS.forEach(k => u.searchParams.delete(k));
    history.replaceState({}, '', u.toString());
  }

  function loadPersisted() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }
  function savePersisted(values) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(values)); } catch {}
  }
  function clearPersisted() {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }

  function setCheckboxCheckedByValue(value, checked) {
    const facet = getSizeFacet();
    if (!facet) return;
    facet.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      if ((cb.value || '').trim() === value) {
        if (cb.checked !== checked) {
          cb.checked = checked;
          cb.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  // Try to trigger the site’s native "Clear all" (for ALL facets)
  function triggerSiteClearAll() {
    // 1) Prefer explicit ID
    let btn = document.getElementById('clear-all-filters');
    // 2) Fallback: class used in provided markup
    if (!btn) btn = document.querySelector('.clear-filters');
    // 3) If found, click it (lets native code reset everything)
    if (btn) { btn.click(); return true; }
    return false;
  }

  // ---------- UI ----------
  function renderSelector() {
    const root = document.getElementById('br-size-selector');
    if (!root) return;

    const scroll = root.querySelector('.br-size-scroll');
    const clearBtn = root.querySelector('.br-size-clear');

    let sizes = readAvailableSizes();
    if (!sizes.length) return; // filters not ready yet; init() will retry

    // Initial selection: prefer URL, then storage, and only keep values that exist in this collection
    const initial = getUrlSelectedSizes();
    const persisted = loadPersisted();
    const existing = new Set(sizes.map(s => s.value));
    const selected = new Set((initial.length ? initial : persisted).filter(v => existing.has(v)));

    // Build chips
    scroll.innerHTML = '';
    sizes.forEach(({ label, value }) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'br-size-btn';
      btn.textContent = label;
      btn.dataset.value = value;
      btn.setAttribute('aria-pressed', selected.has(value) ? 'true' : 'false');

      btn.addEventListener('click', () => {
        const isOn = btn.getAttribute('aria-pressed') === 'true';
        btn.setAttribute('aria-pressed', isOn ? 'false' : 'true');

        if (isOn) selected.delete(value);
        else selected.add(value);

        const arr = Array.from(selected);
        setUrlSelectedSizes(arr);
        savePersisted(arr);
        setCheckboxCheckedByValue(value, !isOn); // drives your existing listeners
        toggleClear();
      });

      scroll.appendChild(btn);
    });

    function toggleClear() { clearBtn.hidden = selected.size === 0; }
    toggleClear();

    // Top chip-bar "Clear" — behave like full "Clear all" (ALL facets)
    clearBtn.addEventListener('click', () => {
      // 1) Trigger native Clear all if available
      const usedNative = triggerSiteClearAll();

      // 2) Ensure URL & local storage are wiped of all filter params
      clearAllFilterParamsInUrl();
      clearPersisted();

      // 3) Reset chip UI
      selected.clear();
      scroll.querySelectorAll('.br-size-btn[aria-pressed="true"]').forEach(b => b.setAttribute('aria-pressed', 'false'));
      toggleClear();

      // 4) If native Clear all didn't exist, fall back to manual size-only uncheck (still fires change)
      if (!usedNative) {
        sizes.forEach(({ value }) => setCheckboxCheckedByValue(value, false));
      }
    });

    // Keep chips in sync if user interacts with the left checkboxes directly
    document.addEventListener('change', (e) => {
      const cb = e.target;
      if (!(cb instanceof HTMLInputElement) || cb.type !== 'checkbox') return;
      const t = cb.closest('.filters-content__group')?.querySelector('.filters-content__title')?.textContent || '';
      if (!/^\s*size\s*$/i.test(t.trim())) return;

      const v = (cb.value || '').trim();
      if (!v) return;
      if (cb.checked) selected.add(v); else selected.delete(v);

      // Update chip UI
      const chip = scroll.querySelector(`.br-size-btn[data-value="${CSS.escape(v)}"]`);
      if (chip) chip.setAttribute('aria-pressed', cb.checked ? 'true' : 'false');

      const arr = Array.from(selected);
      setUrlSelectedSizes(arr);
      savePersisted(arr);
      toggleClear();
    });

    // When site "Clear all" is used, reset our chips as well
    (function bindSiteClearAllSync(){
      const handler = () => {
        selected.clear();
        scroll.querySelectorAll('.br-size-btn').forEach(b => b.setAttribute('aria-pressed', 'false'));
        clearAllFilterParamsInUrl();
        clearPersisted();
        toggleClear();
      };
      // ID first
      const btnById = document.getElementById('clear-all-filters');
      if (btnById) btnById.addEventListener('click', handler);
      // Also hook class fallback if present (and not the same element)
      const btnByClass = document.querySelector('.clear-filters');
      if (btnByClass && btnByClass !== btnById) btnByClass.addEventListener('click', handler);
    })();
  }

  // Wait until filters render; re-try briefly in case Bloomreach paints async
  function init() {
    const tryInit = () => {
      const facet = getSizeFacet();
      const root = document.getElementById('br-size-selector');
      if (facet && root) {
        renderSelector();
      } else {
        setTimeout(tryInit, 150);
      }
    };
    tryInit();

    // Light observer: if filters container replaces its children, rebuild chips
    const filtersContainer = document.querySelector('.filters-content');
    if (filtersContainer && !window.__BR_SIZE_OBS__) {
      window.__BR_SIZE_OBS__ = true;
      const obs = new MutationObserver(() => {
        setTimeout(() => {
          const root = document.getElementById('br-size-selector');
          if (root) renderSelector();
        }, 0);
      });
      obs.observe(filtersContainer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
